/**
 * How a project's environment reaches the program that reads it (§5.7) — the
 * half of that question a repository answers.
 *
 * A dotenv file is the convention of *some* ecosystems, not a universal one.
 * Node, Python, PHP and Ruby tooling loads `.env` by itself; a Spring, Go, .NET
 * or plain Rust project loads nothing of the sort, so a `.env` dropped beside
 * one injects nothing at all — and because the desktop app's review answers to
 * `.gitignore` rather than to what is tracked (§6.5), a repository whose ignore
 * file never expected one gets an untracked diff row carrying whatever secrets
 * it holds.
 *
 * Here rather than in the app because **both sides read a repository now**: the
 * app reads the one on the machine, and the account area reads the one somebody
 * points at in their browser (§5.25). The answer has to be the same on either —
 * a project published from the account page and the same folder opened in the
 * app must not disagree about whether a `.env` is written.
 *
 * What is *not* here is writing: `renderDotenv`, the shell script a JVM project
 * is given, and the one environment a session's shells share all belong to the
 * app, because they are about a sandbox rather than about a repository.
 */
import type { EnvFileMode } from './vocabulary';
import type { RuntimeKind } from './vocabulary';

/**
 * The ecosystems whose environment conventions differ. Wider than `RuntimeKind`
 * on purpose: PHP and .NET have no runtime of their own there (nothing pins
 * their toolchain yet, §10.1) but they still have to be answered here, and
 * `java` covers three stacks that do not agree with each other.
 */
export type EnvEcosystem = 'node' | 'python' | 'php' | 'ruby' | 'jvm' | 'dotnet' | 'go' | 'rust' | 'static' | 'unknown';

/** What the repository says about itself, as much of it as the caller has read. */
export interface EnvEvidence {
  /** Names at the repository root — one directory listing. */
  files?: string[];
  /** The dependency manifest's text: `Cargo.toml`, `go.mod`, `pom.xml`, … */
  manifest?: string;
}

export interface EnvPlan {
  ecosystem: EnvEcosystem;
  /** Repo-relative dotenv to write, or `null` when nothing here would read one. */
  dotenv: string | null;
  /** One clause, for the startup timeline and the settings row. */
  reason: string;
}

/** The only name written: every loader in every ecosystem reads this one. */
export const DOTENV_FILE = '.env';

/** The ecosystems whose mainstream tooling loads a dotenv with no help. */
const LOADS_DOTENV: readonly EnvEcosystem[] = ['node', 'python', 'php', 'ruby'];

/**
 * Dependencies that mean "this project loads a dotenv itself" in an ecosystem
 * where that is the exception rather than the rule. Matched against the
 * manifest's text, not its parse: three build systems per ecosystem spell a
 * dependency three ways, and the name appearing at all is the whole signal.
 */
const DOTENV_DEPS: Partial<Record<EnvEcosystem, RegExp>> = {
  // sqlx reads `.env` for `DATABASE_URL` at *compile* time, so its build needs
  // the file as much as its run does.
  rust: /\bdotenv|\bsqlx\b/i,
  go: /joho\/godotenv/i,
  // Quarkus is the one JVM stack with a dotenv loader of its own. Spring,
  // Micronaut and Helidon bind from the process environment instead.
  jvm: /\bquarkus\b/i,
  dotnet: /DotNetEnv|dotenv\.net/i
};

/**
 * Plain prose, no markup: these reasons are printed in the startup timeline and
 * read back in the settings row that sets the mode.
 */
const WHY: Record<EnvEcosystem, string> = {
  node: 'Node tooling loads .env itself',
  python: 'python-dotenv is how Django, Flask and FastAPI projects read .env',
  php: 'Laravel and Symfony load .env on boot',
  ruby: 'the dotenv gem loads .env before Rails starts',
  // Each of these completes "process environment only — …", so none of them
  // repeats that phrase or opens with another dash.
  jvm: 'nothing in a JVM project reads one; Spring and Micronaut bind from the process environment',
  dotnet: 'nothing in .NET reads one; its configuration binds from the process environment',
  go: 'no dotenv loader in this repository',
  rust: 'no dotenv crate in this repository',
  static: 'a static site has no process to read one',
  unknown: 'the technology is unrecognised, so nothing is written into the tree'
};

/**
 * Committed templates — matched by shape rather than by a fixed list, because
 * the same file is called `.env.example`, `.env.local.example` or `env.dist`
 * depending on the repository.
 */
export function isDotenvTemplate(file: string): boolean {
  return /^\.?env(\.|$)/i.test(file) && /example|sample|template|dist|defaults/i.test(file);
}

const FROM_RUNTIME: Record<RuntimeKind, EnvEcosystem> = {
  node: 'node',
  python: 'python',
  java: 'jvm',
  go: 'go',
  rust: 'rust',
  php: 'php',
  static: 'static',
  mixed: 'unknown',
  unknown: 'unknown'
};

/** Root files naming an ecosystem `RuntimeKind` has no word for. */
function fromFiles(files: readonly string[]): EnvEcosystem | null {
  const lower = files.map((file) => file.toLowerCase());
  if (lower.includes('composer.json')) return 'php';
  if (lower.includes('gemfile')) return 'ruby';
  if (lower.some((file) => /\.(csproj|fsproj|vbproj|sln)$/.test(file))) return 'dotnet';
  return null;
}

/**
 * The runtime is the user's own field (§15), so it wins wherever it says
 * something. `unknown` and `static` are where it does not: a Laravel or ASP.NET
 * repository has no `RuntimeKind`, and detection lands it on `static` the moment
 * the tree happens to hold an `index.html`.
 */
export function ecosystemOf(runtime: RuntimeKind, evidence: EnvEvidence = {}): EnvEcosystem {
  const named = FROM_RUNTIME[runtime];
  if (named !== 'unknown' && named !== 'static') return named;
  return fromFiles(evidence.files ?? []) ?? named;
}

/** What the repository's own files say it should be given — the `auto` answer. */
export function planEnvInjection(runtime: RuntimeKind, evidence: EnvEvidence = {}): EnvPlan {
  const ecosystem = ecosystemOf(runtime, evidence);
  const plan = (dotenv: string | null, reason: string): EnvPlan => ({ ecosystem, dotenv, reason });

  if (ecosystem === 'static') return plan(null, WHY.static);
  if (LOADS_DOTENV.includes(ecosystem)) return plan(DOTENV_FILE, WHY[ecosystem]);

  const dep = DOTENV_DEPS[ecosystem];
  if (dep?.test(evidence.manifest ?? '')) return plan(DOTENV_FILE, 'the repository depends on a dotenv loader');

  // A committed `.env.example` is the repository saying outright that something
  // here loads one — a Makefile, a compose file, a script we cannot see into.
  // Weaker than a dependency, stronger than the table above, which only knows
  // each ecosystem's mainstream.
  if ((evidence.files ?? []).some(isDotenvTemplate)) return plan(DOTENV_FILE, 'the repository ships a dotenv template');

  return plan(null, WHY[ecosystem]);
}

/**
 * What a project's stored mode means with no repository to look at — the boot
 * path and the settings row both ask this. `auto` answers from the ecosystem's
 * convention alone: a project saved before the mode existed has no recorded
 * conclusion, and one whose runtime the user has since corrected should follow
 * the correction rather than a stale reading of the tree.
 */
export function resolveEnvFile(mode: EnvFileMode, runtime: RuntimeKind): EnvPlan {
  const ecosystem = ecosystemOf(runtime);
  if (mode === 'dotenv') return { ecosystem, dotenv: DOTENV_FILE, reason: 'set for this repository' };
  if (mode === 'none') return { ecosystem, dotenv: null, reason: 'set for this repository' };
  return planEnvInjection(runtime);
}

/* ── the dotenv grammar, the reading half ───────────────────────────────── */

export interface EnvEntry {
  key: string;
  value: string;
}

/**
 * `KEY=value` lines, the subset every dotenv loader agrees on: `export` prefix,
 * comments, quoting, and values that run over several lines because a PEM key
 * does. Interpolation (`${OTHER}`) is left verbatim — expanding it here would
 * guess at a resolution order the project's own loader owns.
 *
 * The app's `renderDotenv` is the other half of one grammar: detection reads a
 * developer's file with this, and what boot writes has to read back the same.
 */
export function parseDotenv(text: string): [string, string][] {
  const out: [string, string][] = [];
  const lines = text.split(/\r?\n/);
  for (let i = 0; i < lines.length; i += 1) {
    const line = /^[ \t]*(?:export[ \t]+)?([A-Za-z_][A-Za-z0-9_]*)[ \t]*=(.*)$/.exec(lines[i]);
    if (!line) continue;
    const rest = line[2].trim();
    const quote = rest[0] === '"' || rest[0] === "'" ? rest[0] : '';
    if (!quote) {
      out.push([line[1], rest.replace(/[ \t]+#.*$/, '').trim()]);
      continue;
    }
    let body = rest.slice(1);
    let end = closingQuote(body, quote);
    while (end < 0 && i + 1 < lines.length) {
      i += 1;
      body += `\n${lines[i]}`;
      end = closingQuote(body, quote);
    }
    const value = end < 0 ? body : body.slice(0, end);
    out.push([line[1], quote === '"' ? value.replace(/\\n/g, '\n').replace(/\\(["\\])/g, '$1') : value]);
  }
  return out;
}

function closingQuote(body: string, quote: string): number {
  for (let i = 0; i < body.length; i += 1) {
    if (quote === '"' && body[i] === '\\') i += 1;
    else if (body[i] === quote) return i;
  }
  return -1;
}
